import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import salaryStructureService from "../../services/SalaryStructureService";

export const getSalaryStructures = createAsyncThunk(
    "salaryStructure/getSalaryStructures",
    async(_, { rejectWithValue }) => {
        try {
            const response = await salaryStructureService.GetSalaryStructures();
            const statusCode = response?.data?.StatusCode ?? response?.StatusCode;

            if (statusCode !== 200) {
                throw new Error(response?.data?.Result || response?.data?.Message || "Failed to load salary structures.");
            }

            const raw = response.data?.ResultSet || [];
            return raw.map((item) => ({
                id: item.HSS_STRUCTURE_ID,
                code: item.HSS_STRUCTURE_CODE,
                name: item.HSS_STRUCTURE_NAME,
                versionNo: item.HSS_VERSION_NO,
                categoryCode: item.HSS_CATEGORY_CODE,
                effectiveDate: item.HSS_EFFECTIVE_DATE?.split(" ")[0],
                endDate: item.HSS_END_DATE?.split(" ")[0] || null,
                isActive: item.HSS_IS_ACTIVE === true || item.HSS_IS_ACTIVE === "True",
                remarks: item.HSS_REMARKS || "",
                createdBy: item.HSS_CREATED_BY,
                createdDate: item.HSS_CREATED_DATE,
                updatedBy: item.HSS_UPDATED_BY,
                updatedDate: item.HSS_UPDATED_DATE,
                status: (item.HSS_IS_ACTIVE === true || item.HSS_IS_ACTIVE === "True") ? "Active" : "Inactive",
            }))
        } catch (error) {
            return rejectWithValue(error.response?.data?.Result || error.response?.data?.Message || error.message || "Failed to load salary structures.")
        }
    }
)

export const addSalaryStructure = createAsyncThunk(
  "salaryStructure/addSalaryStructure",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await salaryStructureService.AddSalaryStructure(payload);
      const statusCode = response?.data?.StatusCode ?? response?.StatusCode;
      if (statusCode !== 200) {
        throw new Error(response?.data?.Result || response?.data?.Message || "Failed to add salary structure.");
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Result || error.response?.data?.Message || error.message || "Failed to add salary structure."
      );
    }
  }
);

export const updateSalaryStructure = createAsyncThunk(
  "salaryStructure/updateSalaryStructure",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await salaryStructureService.UpdateSalaryStructure(payload);
      const statusCode = response?.data?.StatusCode ?? response?.StatusCode;
      if (statusCode !== 200) {
        throw new Error(response?.data?.Result || response?.data?.Message || "Failed to update salary structure.");
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Result || error.response?.data?.Message || error.message || "Failed to update salary structure."
      );
    }
  }
);

export const deleteSalaryStructure = createAsyncThunk(
  "salaryStructure/deleteSalaryStructure",
  async (structureId, { rejectWithValue }) => {
    try {
      const response = await salaryStructureService.DeleteSalaryStructure(structureId);
      const statusCode = response?.data?.StatusCode ?? response?.StatusCode;
      if (statusCode !== 200) {
        throw new Error(response?.data?.Result || response?.data?.Message || "Failed to delete salary structure.");
      }
      return structureId;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Result || error.response?.data?.Message || error.message || "Failed to delete salary structure."
      );
    }
  }
);

export const getStructureComponents = createAsyncThunk(
  "salaryStructure/getStructureComponents",
  async (structureId, { rejectWithValue }) => {
    try {
      const response = await salaryStructureService.GetStructureComponents(structureId);
      const statusCode = response?.data?.StatusCode ?? response?.StatusCode;
      if (statusCode !== 200) {
        throw new Error(response?.data?.Result || response?.data?.Message || "Failed to load components.");
      }
      return response.data?.ResultSet || [];
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Result || error.response?.data?.Message || error.message || "Failed to load components."
      );
    }
  }
);

export const addStructureComponent = createAsyncThunk(
  "salaryStructure/addStructureComponent",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await salaryStructureService.AddStructureComponent(payload);
      const statusCode = response?.data?.StatusCode ?? response?.StatusCode;
      if (statusCode !== 200) {
        throw new Error(response?.data?.Result || response?.data?.Message || "Failed to add component.");
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Result || error.response?.data?.Message || error.message || "Failed to add component."
      );
    }
  }
);

export const updateStructureComponent = createAsyncThunk(
  "salaryStructure/updateStructureComponent",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await salaryStructureService.UpdateStructureComponent(payload);
      const statusCode = response?.data?.StatusCode ?? response?.StatusCode;
      if (statusCode !== 200) {
        throw new Error(response?.data?.Result || response?.data?.Message || "Failed to update component.");
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Result || error.response?.data?.Message || error.message || "Failed to update component."
      );
    }
  }
);

export const deleteStructureComponent = createAsyncThunk(
  "salaryStructure/deleteStructureComponent",
  async (structureDetailId, { rejectWithValue }) => {
    try {
      const response = await salaryStructureService.DeleteStructureComponent(structureDetailId);
      const statusCode = response?.data?.StatusCode ?? response?.StatusCode;
      if (statusCode !== 200) {
        throw new Error(response?.data?.Result || response?.data?.Message || "Failed to delete component.");
      }
      return structureDetailId;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Result || error.response?.data?.Message || error.message || "Failed to delete component."
      );
    }
  }
);

const initialState = {
    structures: [],
    selectedStructureComponents: [],
    loading: false,
    saving: false,
    componentsLoading: false,
    error: null,
};

const salaryStructureSlice = createSlice({
  name: "salaryStructure",
  initialState,
  reducers: {
    clearSalaryStructureError: (state) => {
      state.error = null;
    },
    clearSelectedComponents: (state) => {
      state.selectedStructureComponents = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch structures
      .addCase(getSalaryStructures.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getSalaryStructures.fulfilled, (state, action) => {
        state.loading = false;
        state.structures = action.payload;
      })
      .addCase(getSalaryStructures.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch components for structure
      .addCase(getStructureComponents.pending, (state) => {
        state.componentsLoading = true;
      })
      .addCase(getStructureComponents.fulfilled, (state, action) => {
        state.componentsLoading = false;
        state.selectedStructureComponents = action.payload;
      })
      .addCase(getStructureComponents.rejected, (state, action) => {
        state.componentsLoading = false;
        state.error = action.payload;
      })

      // Structure Mutations (Add / Update / Delete)
      .addCase(addSalaryStructure.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(addSalaryStructure.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(addSalaryStructure.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })
      .addCase(updateSalaryStructure.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(updateSalaryStructure.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(updateSalaryStructure.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })
      .addCase(deleteSalaryStructure.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(deleteSalaryStructure.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(deleteSalaryStructure.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      });
  },
});

export const { 
    clearSalaryStructureError, 
    clearSelectedComponents 
} = salaryStructureSlice.actions;

export const selectSalaryStructures = (state) => state.salaryStructure?.structures || [];
export const selectSalaryStructureLoading = (state) => state.salaryStructure?.loading || false;
export const selectSalaryStructureSaving = (state) => state.salaryStructure?.saving || false;
export const selectStructureComponents = (state) => state.salaryStructure?.selectedStructureComponents || [];

export default salaryStructureSlice.reducer;